// src/components/ui/Alert.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";

export type AlertType = "success" | "error" | "info";

const ICONS: Record<AlertType, string> = {
  success: "bi-check-circle-fill",
  error: "bi-exclamation-triangle-fill",
  info: "bi-info-circle-fill",
};

export function Alert({
  type,
  message,
  onClose,
}: {
  type: AlertType;
  message: string;
  onClose?: () => void;
}) {
  return (
    <div
      className={`tp-alert ${type}`}
      role={type === "error" ? "alert" : "status"}
    >
      <i className={`bi ${ICONS[type]}`} />
      <span>{message}</span>
      {onClose && (
        <button
          className="tp-alert-close"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

/**
 * Mensaje temporal de éxito / error para las páginas.
 * Los errores quedan visibles hasta que se cierran; los éxitos se ocultan solos.
 */
export function useFlash() {
  const [flash, setFlash] = useState<{
    type: AlertType;
    message: string;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const show = useCallback((type: AlertType, message: string) => {
    clearTimeout(timer.current);
    setFlash({ type, message });
    if (type === "success") {
      timer.current = setTimeout(() => setFlash(null), 5000);
    }
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const node = flash ? (
    <Alert
      type={flash.type}
      message={flash.message}
      onClose={() => setFlash(null)}
    />
  ) : null;

  return { show, clear: () => setFlash(null), node };
}
